import { LanguageAvailable } from '@shared/services/language';

/**
 * Determines the best matching language for the user based on their preferred languages, the supported languages, and a default fallback language.
 *
 * This function prioritizes matching exact user language preferences (`userLanguages`) with the provided list of supported languages (`supportedLanguages`).
 * If no match is found, it falls back to the provided `defaultLanguage`.
 *
 * @param {readonly string[]} userLanguages - A list of user-preferred language codes, typically provided by the user's browser or application settings.
 * @param {LanguageAvailable[]} supportedLanguages - A list of supported language codes from which to find a match.
 * @param {LanguageAvailable} defaultLanguage - A fallback language to use if no user-preferred match is found.
 * @returns {LanguageAvailable} The best matching language or the `defaultLanguage` if no match is found.
 *
 * @example
 * // Example usage:
 * const userLanguages = ['en-US', 'fr-FR'];
 * const supportedLanguages = ['en', 'fr', 'es'];
 * const defaultLanguage = 'en';
 *
 * const matchedLanguage = findBestMatchingLanguage(userLanguages, supportedLanguages, defaultLanguage);
 * console.log(matchedLanguage); // Output: 'en'
 */
export const findBestMatchingLanguage = (
  userLanguages: readonly string[],
  supportedLanguages: LanguageAvailable[],
  defaultLanguage: LanguageAvailable
): LanguageAvailable => {
  const supportedMap = new Map<string, string>();

  for (const lang of supportedLanguages) {
    const base = lang.split('-')[0] ?? ''.toLowerCase();
    if (!supportedMap.has(base)) {
      supportedMap.set(base, lang);
    }
  }

  for (const lang of userLanguages) {
    const base = lang.split('-')[0] ?? ''.toLowerCase();
    const match = supportedMap.get(base);

    if (match) {
      return match as LanguageAvailable;
    }
  }

  return defaultLanguage;
};

/**
 * Maps a given generic language code to a specific region-based language code, or returns the input
 * if no match is found in the list of supported languages.
 *
 * - If the `languageToCheck` is directly included in the `supportedLanguages`, it returns it.
 * - If not, it searches for a language code in `supportedLanguages` that starts with
 *   `${languageToCheck}-` to provide a regional match.
 * - If no matches are found, it falls back to returning the original `languageToCheck`.
 *
 * @param {string} languageToCheck - The generic language code to check (e.g., "en", "fr").
 * @param {readonly LanguageAvailable[]} supportedLanguages - A readonly array of supported language codes,
 *        including generic (e.g., "en") and region-specific codes (e.g., "en-US", "fr-CA").
 * @returns {LanguageAvailable} - The matched region-specific language code, or the original
 *          `languageToCheck` if no match is found. Always cast as `LanguageAvailable`.
 *
 * @example
 * const languages: readonly LanguageAvailable[] = ['en', 'en-US', 'fr-CA', 'es'];
 *
 * // Example 1: Generic match
 * const result1 = mapGenericLanguageToRegion('en', languages);
 * console.log(result1); // Output: 'en'
 *
 * // Example 2: Regional match
 * const result2 = mapGenericLanguageToRegion('fr', languages);
 * console.log(result2); // Output: 'fr-CA'
 *
 * // Example 3: No match, return input
 * const result3 = mapGenericLanguageToRegion('de', languages);
 * console.log(result3); // Output: 'de'
 *
 */
export const mapGenericLanguageToRegion = (
  languageToCheck: string,
  supportedLanguages: readonly LanguageAvailable[]
): LanguageAvailable => {
  if (supportedLanguages.includes(languageToCheck as LanguageAvailable)) {
    return languageToCheck as LanguageAvailable;
  }

  const regionalMatch = supportedLanguages.find((supported) => supported.startsWith(`${languageToCheck}-`));

  return (regionalMatch || languageToCheck) as LanguageAvailable;
};
